import { atom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback } from 'react'
import type { ChatTurnError } from '../engine/errors'

/**
 * Per-chat turn failure. Holds the latest `ChatTurnError` for each
 * chat so error UI can render by `_tag` instead of scraping message state.
 * Written on every failed turn (submit and resume paths); cleared when a new
 * turn starts for the chat. Keyed by the conversation id current at failure
 * time — an error always ends the stream, so it never races the temp→real id
 * swap.
 */
export const turnErrorsAtom = atom<Record<string, ChatTurnError>>({})

export function setTurnError(prev: Record<string, ChatTurnError>, error: ChatTurnError): Record<string, ChatTurnError> {
  return { ...prev, [error.chatId]: error }
}

export function clearTurnError(prev: Record<string, ChatTurnError>, chatId: string): Record<string, ChatTurnError> {
  if (!(chatId in prev)) return prev
  return Object.fromEntries(Object.entries(prev).filter(([id]) => id !== chatId))
}

/** Latest turn failure for a chat, or undefined when its last turn didn't fail. */
export const useTurnError = (chatId: string | undefined): ChatTurnError | undefined => {
  const errors = useAtomValue(turnErrorsAtom)
  return chatId ? errors[chatId] : undefined
}

export const useSetTurnError = () => {
  const set = useSetAtom(turnErrorsAtom)
  return useCallback((error: ChatTurnError) => set((prev) => setTurnError(prev, error)), [set])
}

export const useClearTurnError = () => {
  const set = useSetAtom(turnErrorsAtom)
  return useCallback((chatId: string) => set((prev) => clearTurnError(prev, chatId)), [set])
}
